import { useEffect, useState, type FormEvent } from "react";

import { track } from "../analytics";
import { asset } from "../content/assets";
import { COUNTRIES } from "../content/countries";
import { EMAIL, SOCIALS, WHATSAPP_URL } from "../content/site";
import { Page } from "../layout/Page";
import { requestErrorMessage } from "../requestError";
import { trpc } from "../trpc";
import { Button } from "../ui/Button";
import { ContactLine } from "../ui/ContactLine";
import { Field } from "../ui/Field";
import { Section } from "../ui/Section";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The five interest checkboxes, verbatim from the old form. */
const INTERESTS = [
  "I want to Meditate",
  "I want to Discuss",
  "I want Swarved",
  "I want Magazine",
  "Others",
] as const;

/** Sadafaldeo Maharaj's portrait, shown beside the intro on desktop. */
const SWAMIJI_PORTRAIT = asset(
  "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/r/2556-1436-896-1436-832-0/B08C6DDD1B097C14.png?v=250703010154",
);

interface ContactDraft {
  name: string;
  country: string;
  email: string;
  mobile: string;
  interests: string[];
  message: string;
}

const EMPTY_DRAFT: ContactDraft = {
  name: "",
  country: "Singapore",
  email: "",
  mobile: "",
  interests: [],
  message: "",
};

function firstProblem(d: ContactDraft): string | null {
  if (!d.name.trim()) {
    return "Please enter your name.";
  }
  if (!EMAIL_RE.test(d.email.trim())) {
    return "Please enter a valid email address.";
  }
  return null;
}

function buildMessage(d: ContactDraft): string {
  return [
    `Country: ${d.country}`,
    `Mobile: ${d.mobile.trim()}`,
    `Interests: ${INTERESTS.filter((i) => d.interests.includes(i)).join(", ")}`,
    "",
    d.message.trim(),
  ].join("\n");
}

/** A small WhatsApp glyph, inlined so the intro paragraph's link can carry it. */
function WhatsappIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="currentColor"
      aria-hidden="true"
      className="inline-block align-text-bottom"
    >
      <path d="M12 2a10 10 0 0 0-8.6 15l-1.3 4.8 5-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8s-.4-.1-.6.1-.6.8-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.3-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4 5.2 5.2 0 0 0 3.2.6 2.7 2.7 0 0 0 1.8-1.3c.2-.6.2-1.1.2-1.2-.1-.2-.3-.3-.5-.4z" />
    </svg>
  );
}

const LINK_CLASS = "text-teal-deep underline underline-offset-4";

export function ContactPage() {
  const [draft, setDraft] = useState<ContactDraft>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);
  const submit = trpc.submitContact.useMutation();

  useEffect(() => {
    track("contact_viewed");
  }, []);

  function toggleInterest(value: string) {
    setDraft((d) => ({
      ...d,
      interests: d.interests.includes(value)
        ? d.interests.filter((v) => v !== value)
        : [...d.interests, value],
    }));
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const problem = firstProblem(draft);
    setError(problem);
    if (problem) {
      return;
    }
    submit.mutate(
      {
        name: draft.name.trim(),
        contact: draft.email.trim(),
        message: buildMessage(draft),
      },
      {
        onSuccess: () => {
          track("contact_submitted");
        },
        onError: (err) =>
          setError(
            requestErrorMessage(
              err,
              "Sorry, we couldn't send your message just now. Please try again in a moment.",
            ),
          ),
      },
    );
  }

  const busy = submit.isPending;

  if (submit.isSuccess) {
    return (
      <Page title="Contact" heading="Contact Us">
        <Section>
          <div role="status" className="max-w-xl">
            <p className="eyebrow">Thank you</p>
            <p className="mt-3 text-lg text-ink">
              Thank you. We&apos;ve received your message and will reply by
              email.
            </p>
            <div className="mt-6">
              <Button href="/">Home</Button>
            </div>
          </div>
        </Section>
      </Page>
    );
  }

  return (
    <Page title="Contact" heading="Contact Us">
      <Section>
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:items-start">
          <div>
            <img
              src={SWAMIJI_PORTRAIT}
              alt="Swamiji"
              className="mb-6 hidden w-full max-w-xs rounded-lg object-cover md:block"
              loading="lazy"
            />
            <p className="max-w-xl text-ink-soft">
              Please visit our{" "}
              <a href="/activities" className={LINK_CLASS}>
                Activities
              </a>{" "}
              and{" "}
              <a href="/resources" className={LINK_CLASS}>
                Resources
              </a>{" "}
              pages, or follow us on{" "}
              {SOCIALS.facebookUrl ? (
                <a
                  href={SOCIALS.facebookUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={LINK_CLASS}
                >
                  Facebook
                </a>
              ) : null}
              {SOCIALS.youtubeUrl ? (
                <>
                  ,{" "}
                  <a
                    href={SOCIALS.youtubeUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={LINK_CLASS}
                  >
                    YouTube
                  </a>
                </>
              ) : null}
              {SOCIALS.twitterUrl ? (
                <>
                  ,{" "}
                  <a
                    href={SOCIALS.twitterUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={LINK_CLASS}
                  >
                    Twitter
                  </a>
                </>
              ) : null}
              {SOCIALS.instagramUrl ? (
                <>
                  {" "}
                  and{" "}
                  <a
                    href={SOCIALS.instagramUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={LINK_CLASS}
                  >
                    Instagram
                  </a>
                </>
              ) : null}{" "}
              to know more. Additionally, you may write to us at{" "}
              <a href={`mailto:${EMAIL}`} className={LINK_CLASS}>
                {EMAIL}
              </a>{" "}
              or contact us at{" "}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className={`${LINK_CLASS} inline-flex items-center gap-1`}
              >
                WhatsApp <WhatsappIcon />
              </a>
              .
            </p>
            <ContactLine className="mt-4 max-w-xl" />
          </div>

          <form onSubmit={onSubmit} noValidate className="grid gap-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" htmlFor="contact-name">
                <input
                  id="contact-name"
                  className="field"
                  autoComplete="name"
                  value={draft.name}
                  disabled={busy}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, name: e.target.value }))
                  }
                />
              </Field>
              <Field label="Country" htmlFor="contact-country">
                <select
                  id="contact-country"
                  className="field"
                  value={draft.country}
                  disabled={busy}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, country: e.target.value }))
                  }
                >
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Email" htmlFor="contact-email">
                <input
                  id="contact-email"
                  className="field"
                  type="email"
                  autoComplete="email"
                  value={draft.email}
                  disabled={busy}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, email: e.target.value }))
                  }
                />
              </Field>
              <Field label="Mobile" htmlFor="contact-mobile">
                <input
                  id="contact-mobile"
                  className="field"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={draft.mobile}
                  disabled={busy}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, mobile: e.target.value }))
                  }
                />
              </Field>
            </div>

            <fieldset>
              <legend className="text-sm font-semibold text-ink">
                I&apos;m interested in
              </legend>
              <div className="mt-2.5 grid gap-x-4 gap-y-2 sm:grid-cols-2">
                {INTERESTS.map((interest) => (
                  <label
                    key={interest}
                    className="flex min-h-11 items-center gap-2 text-sm text-ink"
                  >
                    <input
                      type="checkbox"
                      checked={draft.interests.includes(interest)}
                      disabled={busy}
                      onChange={() => toggleInterest(interest)}
                      className="h-5 w-5 shrink-0 accent-magenta"
                    />
                    {interest}
                  </label>
                ))}
              </div>
            </fieldset>

            <Field label="Message" htmlFor="contact-message">
              <textarea
                id="contact-message"
                className="field"
                rows={5}
                maxLength={2000}
                value={draft.message}
                disabled={busy}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, message: e.target.value }))
                }
              />
            </Field>

            {error ? (
              <p
                role="alert"
                className="rounded-md border border-crimson/40 bg-crimson/5 p-3 text-sm text-crimson"
              >
                {error}
              </p>
            ) : null}
            {submit.error && !error ? (
              <p
                role="alert"
                className="rounded-md border border-crimson/40 bg-crimson/5 p-3 text-sm text-crimson"
              >
                {submit.error.message}
              </p>
            ) : null}

            <div>
              <Button type="submit" disabled={busy}>
                {busy ? "Sending" : "Submit"}
              </Button>
            </div>
          </form>
        </div>
      </Section>
    </Page>
  );
}
